import axios from 'axios';

if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  let devHttpSeq = 0;
  axios.interceptors.request.use((config) => {
    const rid = ++devHttpSeq;
    const method = (config.method || 'get').toUpperCase();
    const url = `${config.baseURL || ''}${config.url || ''}`;
    console.debug(
      `[http ${rid}] ${method} ${url} t=${performance.now().toFixed(1)} ${new Date().toISOString()}`
    );
    return config;
  });
}

const baseHeaders = {
  'Content-Type': 'application/json'
};

const API_BASE_URL = process.env.REACT_APP_API_URL;

const readAccessTokenFromStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem('access_token');
  } catch (_error) {
    return null;
  }
};

const toAuthorizationHeader = (value) => {
  if (!value) return null;
  const normalized = String(value).trim();
  if (!normalized) return null;
  return normalized.startsWith('Bearer ') ? normalized : `Bearer ${normalized}`;
};

let tokenHeader = toAuthorizationHeader(readAccessTokenFromStorage());

const syncAxiosAuthorizationHeader = (authorization) => {
  if (authorization) {
    axios.defaults.headers.common.Authorization = authorization;
    return;
  }
  delete axios.defaults.headers.common.Authorization;
};

export const setToken = (token) => {
  tokenHeader = toAuthorizationHeader(token);
  syncAxiosAuthorizationHeader(tokenHeader);
};

export const clearToken = () => {
  tokenHeader = null;
  syncAxiosAuthorizationHeader(null);
};

const getAuthorizationHeader = (additional = {}) => {
  const additionalAuthorization = toAuthorizationHeader(additional.Authorization);
  if (additionalAuthorization) return additionalAuthorization;

  const axiosAuthorization = toAuthorizationHeader(axios.defaults.headers?.common?.Authorization);
  if (axiosAuthorization) return axiosAuthorization;

  if (tokenHeader) return tokenHeader;

  const storageAuthorization = toAuthorizationHeader(readAccessTokenFromStorage());
  if (!storageAuthorization) return null;
  tokenHeader = storageAuthorization;
  return storageAuthorization;
};

const getAuthHeaders = (additional = {}) => {
  const authorization = getAuthorizationHeader(additional);
  return {
    ...baseHeaders,
    ...(authorization ? { Authorization: authorization } : {}),
    ...additional,
  };
};

// Multipart: do not set Content-Type so the browser sets multipart/form-data with boundary.
// Explicitly include current auth from axios defaults so multipart requests are consistent and
// resilient even if defaults are not merged at request time.
const getMultipartHeaders = (additional = {}) => {
  const authorization = getAuthorizationHeader(additional);
  return {
    ...(authorization ? { Authorization: authorization } : {}),
    ...additional,
  };
};

export const getRequest = async (url, additionalHeaders = {}, queryParams = {}) => {
  const config = { 
    headers: getAuthHeaders(additionalHeaders),
    params: queryParams
  };
  const response = await axios.get(`${API_BASE_URL}${url}`, config);
  return response;
};

export const postRequest = async (url, data = {}, additionalHeaders = {}) => {
  const config = { headers: getAuthHeaders(additionalHeaders) };
  const response = await axios.post(`${API_BASE_URL}${url}`, data, config);
  return response;
};

export const publicPostRequest = async (url, data = {}, additionalHeaders = {}) => {
  const config = { headers: { ...baseHeaders, ...additionalHeaders } };
  const response = await axios.post(`${API_BASE_URL}${url}`, data, config);
  return response;
};

export const publicGetRequest = async (url, queryParams = {}, additionalHeaders = {}) => {
  const config = {
    headers: { ...baseHeaders, ...additionalHeaders },
    params: queryParams,
  };
  return axios.get(`${API_BASE_URL}${url}`, config);
};

export const patchRequest = async (url, data = {}, additionalHeaders = {}) => {
  const config = { headers: getAuthHeaders(additionalHeaders) };
  const response = await axios.patch(`${API_BASE_URL}${url}`, data, config);
  return response;
}

export const putRequest = async (url, data = {}, additionalHeaders = {}) => {
  const config = { headers: getAuthHeaders(additionalHeaders) };
  const response = await axios.put(`${API_BASE_URL}${url}`, data, config);
  return response;
};

export const deleteRequest = async (url, additionalHeaders = {}) => {
  const config = { headers: getAuthHeaders(additionalHeaders) };
  const response = await axios.delete(`${API_BASE_URL}${url}`, config);
  return response;
};

export const patchMultipartRequest = async (url, formData, additionalHeaders = {}) => {
  const config = { headers: getMultipartHeaders(additionalHeaders) };
  return axios.patch(`${API_BASE_URL}${url}`, formData, config);
};

export const postMultipartRequest = async (url, formData, additionalHeaders = {}) => {
  const config = { headers: getMultipartHeaders(additionalHeaders) };
  return axios.post(`${API_BASE_URL}${url}`, formData, config);
};
